import * as R from "ramda";

import * as AnswerContext from "reducers/answerContexts";
import { EMPTY_ARRAY, compact, isBlank, sumFloats } from "utils/func";
import * as LocationUtils from "utils/location";
import { getStartAddress } from "utils/route";
import { parseTime } from "utils/time";

export const getIsZoningEnabled = R.path(["answers", "zoning_enabled"]);

export const getZoningFieldIDs = R.pathOr(EMPTY_ARRAY, ["user_pages", "zoning", "list"]);
const getZoningFieldIdents = R.pathOr(EMPTY_ARRAY, ["user_pages", "zoning", "list"]);
const identToKey = (ident) => ident.split(":")[1];
const getZoningFieldKeys = R.pipe(getZoningFieldIdents, R.map(identToKey));

export const getUseCodeConditionAnswers = (record) => {
  const zoningKeys = R.difference(getZoningFieldKeys(record), ["zoning"]);
  return R.pick(zoningKeys, record.answers);
};

export const getCurrentRequirementApplications = R.pipe(
  R.prop("requirement_applications"),
  R.values,
);

export const hasAddress = (project) => {
  const value = project.answers?.address;
  return !!value?.latitude;
};

export const getZoning = (project) => project?.answers?.zoning || defaultZoning;

const defaultZoning = { zones: [], parcels: [] };

export const getLogEntries = R.pipe(
  R.propOr({}, "log_entries_json"),
  R.values,
  R.sortBy(R.prop("timestamp")),
  R.map(R.over(R.lensProp("timestamp"), parseTime)),
);

const getProjectRequirementSectionList = (project, requirement) =>
  R.path(["user_pages", "requirements", R.prop("key", requirement)], project);

export const getProjectRequirementFieldIDs = (project, requirement) =>
  R.pipe(
    R.propOr({}, "sections"),
    R.values,
    R.flatten,
    R.uniq,
  )(getProjectRequirementSectionList(project, requirement));

export const getRequirementRequirementApplicationID = R.prop("requirement_id");

export const getRequirementApplicationByRequirementID = (project, requirementID) =>
  R.pipe(
    R.prop("requirement_applications"),
    R.values,
    R.find(R.propEq(Number(requirementID), "requirement_id")),
  )(project);

export const getApplicant = R.prop("user");
export const getRequirementApplications = R.pipe(R.prop("requirement_applications"), R.values);

export const getRequirementApplication = (project, id) =>
  R.path(["requirement_applications", id], project);
export const getLockedFieldIDs = R.propOr([], "locked_field_ids");
export const getStatus = R.prop("status");
export const isInProgress = R.propEq("In-Progress", "status");
export const isProjectLoaded = R.has("answers");
export const getAddress = R.prop("business_address");
export const getName = R.path(["answers", "project_name"]);
export const getUseCodeName = R.prop("use_code_name");
export const getGuideType = R.path(["guide", "guide_type"]);
export const getTotalFees = R.pipe(R.prop("calculated_fee_totals"), R.values, R.sum);
export const getRequirementIDs = R.propOr(EMPTY_ARRAY, "requirement_ids");
export const getEventLocations = R.pathOr(EMPTY_ARRAY, ["answers", "event_locations"]);
export const getEventRoutes = R.pathOr(EMPTY_ARRAY, ["answers", "event_routes"]);

const getEventTime = (project, type) =>
  R.pipe(
    R.pathOr([], ["answers", "event_times"]),
    R.pluck(type),
    R.map(parseTime),
    R.reduce(R.min, Infinity),
    R.ifElse(R.equals(Infinity), R.always(undefined), R.identity),
  )(project);

export const getEventStart = (project) => getEventTime(project, "start_time");

export const getLegacyEventPrimaryAddress = (project) => {
  const locations = getEventLocations(project);
  if (!isBlank(locations)) return R.pipe(R.head, LocationUtils.getAddress)(locations);

  const routes = getEventRoutes(project);
  if (!isBlank(routes)) return R.pipe(R.head, getStartAddress)(routes);

  return null;
};

export const getFeeTotals = R.propOr({}, "calculated_fee_totals");

export const isEvent = R.pipe(getGuideType, R.equals("special_events"));

export const isAnswerBlankForField = (project, field) => {
  const a = AnswerContext.getAnswerForField(project, field);

  if (Array.isArray(a)) return R.pipe(R.flatten, compact, R.isEmpty)(a);

  if (a instanceof Object) return R.pipe(R.values, compact, R.flatten, R.isEmpty)(a);

  if (typeof a === "string" || a instanceof String) return R.isEmpty(a);

  return R.isNil(a);
};

export const filterUnansweredFields = R.curry((project, fields) =>
  R.reject((f) => isAnswerBlankForField(project, f), fields),
);

export const areAnyFieldsAnswered = (project, fields) =>
  R.any((f) => !isAnswerBlankForField(project, f), fields);

export const getFeesTotal = (project) => sumFloats(R.values(getFeeTotals(project)));
